<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    v-model="isOpen"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>

    <v-card style="width: 560px" v-if="isOpen">
      <v-card-text height="800px" class="px-8 pt-8">
        <div class="mb-8 d-flex align-center">
          <h4 class="font-weight-light">
            {{ $t("heading.onboarding.instanceSource.title") }}
          </h4>
          <v-btn x-small icon @click="$emit('remove-modal')" class="ml-auto">
            <v-icon size="24">$close</v-icon>
          </v-btn>
        </div>

        <div class="selection-box">
          <checkbox-big
            v-if="canInstallWordpress"
            class="pa-6"
            :checked="selectedInstallationType === 'wordpress'"
            @check="selectedInstallationType = 'wordpress'"
          >
            <template v-slot:custom>
              <div class="text-left">
                <div class="d-flex align-center mb-2">
                  <v-icon size="24" color="primary">
                    $wordpress
                  </v-icon>
                  <h5 class="p-1 font-weight-bold ml-2 mb-0">
                    {{ $t("heading.onboarding.instanceSource.wordpress.title") }}
                  </h5>
                </div>
                <p class="p-2 font-weight-light mb-0 gray--text text--darken-1">
                  {{ $t("heading.onboarding.instanceSource.wordpress.subtitle") }}
                </p>
              </div>
            </template>
          </checkbox-big>

          <checkbox-big
            v-if="canInstallFromGit"
            class="pa-6"
            :checked="selectedInstallationType === 'git'"
            @check="selectedInstallationType = 'git'"
          >
            <template v-slot:custom>
              <div class="text-left">
                <div class="d-flex align-center mb-2">
                  <v-icon size="24" color="primary">
                    $github
                  </v-icon>
                  <h5 class="p-1 font-weight-bold ml-2 mb-0">
                    {{ $t("heading.onboarding.instanceSource.git.title") }}
                  </h5>
                </div>
                <p class="p-2 font-weight-light mb-0 gray--text text--darken-1">
                  {{ $t("heading.onboarding.instanceSource.git.subtitle") }}
                </p>
              </div>
            </template>
          </checkbox-big>

          <checkbox-big
            v-if="canInstallFromFiles"
            class="pa-6"
            :checked="selectedInstallationType === 'files'"
            @check="selectedInstallationType = 'files'"
          >
            <template v-slot:custom>
              <div class="text-left">
                <div class="d-flex align-center mb-2">
                  <v-icon size="24" color="primary">
                    $filetext
                  </v-icon>
                  <h5 class="p-1 font-weight-bold ml-2 mb-0">
                    {{ $t("heading.onboarding.instanceSource.zip.title") }}
                  </h5>
                </div>
                <p class="p-2 font-weight-light mb-0 gray--text text--darken-1">
                  {{ $t("heading.onboarding.instanceSource.zip.subtitle") }}
                </p>
              </div>
            </template>
          </checkbox-big>

          <checkbox-big
            v-if="canInstallFromPanelalphaGo"
            class="pa-6"
            :checked="selectedInstallationType === 'go'"
            @check="selectedInstallationType = 'go'"
          >
            <template v-slot:custom>
              <div class="text-left">
                <div class="d-flex align-center">
                  <v-icon size="24" color="primary">
                    $globe
                  </v-icon>
                  <h5 class="p-1 font-weight-bold ml-2 mb-0">
                    {{ $t("heading.onboarding.instanceSource.go.title") }}
                  </h5>
                </div>
              </div>
            </template>
          </checkbox-big>
        </div>
      </v-card-text>

      <v-card-actions
        class="pt-8 d-flex align-center justify-space-between"
        :class="$vuetify.breakpoint.width < 550 ? 'flex-column-reverse' : ''"
      >
        <div :class="$vuetify.breakpoint.width < 550 ? 'mt-4 w-100' : ''">
          <step-indicator :currentStep="1" :maxSteps="4"/>
        </div>
        <div
          class="d-flex align-center"
          :class="$vuetify.breakpoint.width < 550 ? 'flex-column-reverse w-100' : ''"
        >
          <v-btn
            large
            @click="$emit('remove-modal')"
            text
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            class="mr-3"
          >
            <span class="font-weight-bold gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
          <v-btn
            large
            @click="$emit('next', selectedInstallationType)"
            color="primary"
            :block="$vuetify.breakpoint.width < 550 ? true : false"
            elevation="0"
            :loading="loading"
          >
            <span class="font-weight-bold">
              {{ $t("button.continue") }}
            </span>
            <v-icon class="ml-2 mr-0" size="20">$arrowright</v-icon>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StepIndicator from "../StepIndicator.vue";
import CheckboxBig from "../buttons/CheckboxBig.vue";

export default {
  name: "OnboardingChooseInstallationTypeModal",
  components: {
    CheckboxBig,
    StepIndicator,
  },
  props: {
    isOpen: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
    canInstallWordpress: {type: Boolean, default: false},
    canInstallFromGit: {type: Boolean, default: false},
    canInstallFromFiles: {type: Boolean, default: false},
    canInstallFromPanelalphaGo: {type: Boolean, default: false},
  },
  data() {
    return {
      selectedInstallationType: 'wordpress'
    };
  },
  watch: {
    isOpen: function (value) {
      if (this.canInstallWordpress) {
        this.selectedInstallationType = 'wordpress'
      } else if (this.canInstallFromGit) {
        this.selectedInstallationType = 'git'
      } else if (this.canInstallFromFiles) {
        this.selectedInstallationType = 'files'
      } else if (this.canInstallFromPanelalphaGo) {
        this.selectedInstallationType = 'go'
      }

      if (value) {
        this.$nextTick(() => {
          this.$store.dispatch("lockBodyScroll");
        });
      } else {
        this.$store.dispatch("unlockBodyScroll");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.v-card::v-deep {
  .selection-box {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .checkbox-big {
      width: 100%;
      min-width: unset;
      align-items: flex-start;
      justify-content: flex-start;
      overflow: visible;
      border-radius: 8px;

      &__title:empty {
        display: none;
      }

      &::after {
        top: -1px;
        left: -1px;
        border-radius: 8px;
        box-sizing: border-box;
      }
    }
  }

  .go-disabled {
    width: 100%;
    opacity: 0.45;
    pointer-events: none;
  }

  .v-card__text {
    -webkit-line-clamp: unset !important;
  }

  @media (max-width: 480px) {
    .v-card__text,
    .v-card__actions {
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }
}
</style>
